/**
 * Helper Dashboard
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { helpersAPI, requestsAPI } from '../../services/api';
import { X } from 'lucide-react';
import * as socketService from '../../services/socket';
import { useNotifications } from '../../context/NotificationContext';
import {
  MapPin, Star, CheckCircle, Clock, ArrowRight, ToggleLeft, ToggleRight,
  Calendar, Ship, AlertCircle, MessageCircle, User,
} from 'lucide-react';
import { format } from 'date-fns';
import DashboardProfileCard from '../../components/common/DashboardProfileCard';

function Dashboard() {
  const { user } = useAuth();
  const { showToast } = useNotifications();
  const [loading, setLoading] = useState(true);
  const [helperProfile, setHelperProfile] = useState(null);
  const [availableRequests, setAvailableRequests] = useState([]);
  const [myActiveJobs, setMyActiveJobs] = useState([]);
  const [stats, setStats] = useState({ completedServices: 0, rating: 0, totalEarnings: 0 });

  useEffect(() => { loadDashboardData(); }, []);

  const loadDashboardData = async () => {
    try {
      const [profileData, requestsData] = await Promise.all([
        helpersAPI.getMyProfile(), requestsAPI.getAvailableRequests(),
      ]);
      setHelperProfile(profileData.profile);
      const allRequests = requestsData.data || requestsData.requests || [];
      const activeJobs = allRequests.filter(r => r.application_status === 'selected' && (r.status === 'matched' || r.status === 'active'));
      const available = allRequests.filter(r => r.application_status !== 'selected' || (r.status !== 'matched' && r.status !== 'active'));
      setMyActiveJobs(activeJobs);
      setAvailableRequests(available.slice(0, 5));
      if (profileData.profile) {
        setStats({
          completedServices: profileData.profile.completed_services || 0,
          rating: profileData.profile.average_rating || 0,
          totalEarnings: profileData.profile.total_earnings || 0,
        });
      }
    } catch (error) { console.error('Failed to load dashboard data:', error); }
    finally { setLoading(false); }
  };

  const handleToggleAvailability = async () => {
    const newStatus = !helperProfile?.available_now;
    try {
      await helpersAPI.toggleAvailability(newStatus);
      socketService.toggleAvailability(newStatus);
      setHelperProfile(prev => ({ ...prev, available_now: newStatus }));
      showToast(newStatus ? 'You are now available for requests' : 'You are now offline', 'success');
    } catch (error) { showToast('Failed to update availability', 'error'); }
  };

  const handleDismissRequest = async (e, requestId) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await requestsAPI.dismissRequest(requestId);
      setAvailableRequests(prev => prev.filter(r => r.id !== requestId));
      showToast('Request hidden', 'success');
    } catch (error) { showToast('Failed to hide request', 'error'); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="spinner w-8 h-8" /></div>;

  const hasServiceAreas = helperProfile?.service_areas?.length > 0;

  return (
    <div className="space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-display font-bold text-navy-900">Welcome back, {user?.first_name}!</h1>
          <p className="text-navy-600 mt-1">Manage your services and find new opportunities</p>
        </div>
        <button onClick={handleToggleAvailability} className={`mt-4 sm:mt-0 flex items-center px-4 py-2 rounded-full font-medium transition-colors ${helperProfile?.available_now ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
          {helperProfile?.available_now ? <><ToggleRight className="h-5 w-5 mr-2" />Available Now</> : <><ToggleLeft className="h-5 w-5 mr-2" />Offline</>}
        </button>
      </div>

      {!hasServiceAreas && (
        <div className="card bg-red-50 border-2 border-red-300">
          <div className="flex items-start">
            <AlertCircle className="h-6 w-6 text-red-600 mt-0.5" />
            <div className="ml-3">
              <h3 className="font-semibold text-red-900">⚠️ Action Required: Add Service Areas</h3>
              <p className="text-red-700 mt-1">You won't appear in search results or receive job notifications until you add service areas. Add ALL ports near your location — the more you add, the more opportunities you'll see!</p>
              <Link to="/profile" className="inline-flex items-center mt-3 px-4 py-2 bg-red-600 text-white rounded-full hover:bg-red-700 font-medium">Add Service Areas Now<ArrowRight className="h-4 w-4 ml-1" /></Link>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <div className="card"><div className="flex items-center"><div className="h-12 w-12 bg-green-100 rounded-lg flex items-center justify-center"><CheckCircle className="h-6 w-6 text-green-600" /></div><div className="ml-4"><p className="text-sm text-navy-500">Completed Services</p><p className="text-2xl font-bold text-navy-900">{stats.completedServices}</p></div></div></div>
        <div className="card"><div className="flex items-center"><div className="h-12 w-12 bg-gold-100 rounded-lg flex items-center justify-center"><Star className="h-6 w-6 text-gold-600" /></div><div className="ml-4"><p className="text-sm text-navy-500">Average Rating</p><p className="text-2xl font-bold text-navy-900">{stats.rating > 0 ? stats.rating.toFixed(1) : '—'}</p></div></div></div>
      </div>

      {myActiveJobs.length > 0 && (
        <div className="card bg-green-50 border border-green-200">
          <div className="flex items-center justify-between mb-6"><h2 className="text-lg font-display font-semibold text-green-900 flex items-center"><CheckCircle className="h-5 w-5 mr-2" />My Active Jobs ({myActiveJobs.length})</h2></div>
          <div className="space-y-4">
            {myActiveJobs.map((job) => (
              <div key={job.id} className="p-4 bg-white rounded-lg border border-green-200">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2"><MapPin className="h-4 w-4 text-green-600" /><p className="font-medium text-navy-900">{job.port_name}</p><span className="inline-flex items-center px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full"><Star className="h-3 w-3 mr-1 text-gold-500" />Selected!</span></div>
                    <div className="mt-2 flex flex-wrap gap-3 text-sm text-navy-600">
                      <span className="flex items-center"><Calendar className="h-4 w-4 mr-1" />{format(new Date(job.scheduled_date), 'MMM d')}</span>
                      <span className="flex items-center"><Clock className="h-4 w-4 mr-1" />{job.scheduled_time || job.eta_from}</span>
                      {(job.length_m || job.yacht_length) && <span className="flex items-center"><Ship className="h-4 w-4 mr-1" />{job.length_m || job.yacht_length}m{job.yacht_type && <span className="ml-1 text-navy-400 capitalize">({job.yacht_type})</span>}</span>}
                    </div>
                    <div className="mt-3 flex items-center text-sm text-navy-600"><User className="h-4 w-4 mr-1" /><span>Owner: {job.owner_first_name} {job.owner_last_name}</span></div>
                  </div>
                  <div className="flex flex-col space-y-2 ml-4"><Link to={`/helper/requests/${job.id}`} className="btn-primary py-2 px-4 text-sm flex items-center"><MessageCircle className="h-4 w-4 mr-1" />Chat & Details</Link></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="card">
        <div className="flex items-center justify-between mb-6"><h2 className="text-lg font-display font-semibold text-navy-900">Available Requests</h2><Link to="/helper/requests" className="text-primary-600 hover:text-primary-700 text-sm font-medium">View all</Link></div>
        {availableRequests.length === 0 ? (
          <div className="text-center py-8"><MapPin className="h-12 w-12 text-gray-300 mx-auto mb-3" /><p className="text-navy-500">No requests in your area right now</p><p className="text-sm text-navy-400 mt-1">Check back soon or expand your service areas</p></div>
        ) : (
          <div className="space-y-4">
            {availableRequests.map((request) => (
              <Link key={request.id} to={`/helper/requests/${request.id}`} className="block p-4 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center space-x-2"><MapPin className="h-4 w-4 text-primary-600" /><p className="font-medium text-navy-900">{request.port_name}</p></div>
                    <div className="mt-2 flex flex-wrap gap-3 text-sm text-navy-600">
                      <span className="flex items-center"><Calendar className="h-4 w-4 mr-1" />{format(new Date(request.scheduled_date), 'MMM d')}</span>
                      <span className="flex items-center"><Clock className="h-4 w-4 mr-1" />{request.scheduled_time || request.eta_from}</span>
                      {(request.length_m || request.yacht_length) && <span className="flex items-center"><Ship className="h-4 w-4 mr-1" />{request.length_m || request.yacht_length}m{request.yacht_type && <span className="ml-1 text-navy-400 capitalize">({request.yacht_type})</span>}</span>}
                    </div>
                    {(request.special_notes || request.notes) && <p className="mt-2 text-xs text-amber-700 bg-amber-50 px-2 py-1 rounded inline-block">⚠️ {(request.special_notes || request.notes).substring(0, 50)}{(request.special_notes || request.notes).length > 50 ? '...' : ''}</p>}
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className={`badge ${request.request_type === 'real_time' ? 'bg-gold-100 text-gold-800' : 'bg-primary-100 text-primary-800'}`}>{request.request_type === 'real_time' ? 'Urgent' : 'Scheduled'}</span>
                    <button onClick={(e) => handleDismissRequest(e, request.id)} className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-colors" title="Hide this request"><X className="h-4 w-4" /></button>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      {hasServiceAreas && (
        <div className="card">
          <div className="flex items-center justify-between mb-4"><h2 className="text-lg font-display font-semibold text-navy-900">Your Service Areas</h2><Link to="/profile" className="text-primary-600 hover:text-primary-700 text-sm font-medium">Manage</Link></div>
          <div className="flex flex-wrap gap-2">{helperProfile.service_areas.map((area, index) => (<span key={index} className="inline-flex items-center px-3 py-1.5 bg-gray-100 text-navy-700 rounded-full text-sm"><MapPin className="h-3.5 w-3.5 mr-1.5" />{area.port_name || area.name}</span>))}</div>
        </div>
      )}

      {/* My Profile (Read-Only) */}
      <DashboardProfileCard />
    </div>
  );
}

export default Dashboard;
